/**
 * Capacity Snapshot Builder
 *
 * Builds a complete capacity snapshot from raw shift, visit, and calendar block data.
 * This is the core engine that aggregates data per provider per day.
 */

import {
  ProviderCapacityDay,
  ShiftData,
  VisitData,
  CalendarBlockData,
  ProviderData,
} from '../types/capacity.types';
import { formatDateKey } from '../utils/dateHelpers';
import {
  aggregateShiftMinutes,
  aggregateVisitMinutes,
  aggregateBreakMinutes,
  getUniqueProviderIds,
} from './aggregators';
import {
  calculateUtilization,
  calculateRemainingMinutes,
  isOverCapacity,
  assessRiskLevel,
} from './utilizationCalculator';

/**
 * Build capacity snapshot for given date range
 *
 * @param shifts - Array of shift data
 * @param visits - Array of visit data
 * @param calendarBlocks - Array of calendar block (break) data
 * @param providers - Array of provider metadata
 * @param dateRange - Array of dates to calculate capacity for
 * @returns Array of ProviderCapacityDay objects
 */
export function buildCapacitySnapshot(
  shifts: ShiftData[],
  visits: VisitData[],
  calendarBlocks: CalendarBlockData[],
  providers: Map<number, ProviderData>,
  dateRange: Date[]
): ProviderCapacityDay[] {
  const snapshot: ProviderCapacityDay[] = [];

  // Filter to only confirmed shifts
  const confirmedShifts = shifts.filter((shift) => !shift.status || shift.status === 'confirmed');

  // Filter to only non-canceled visits
  const activeVisits = visits.filter((visit) => !visit.eventStatus || visit.eventStatus !== 'canceled');

  // Filter to only scheduled break-type calendar blocks
  const activeBreaks = calendarBlocks.filter(
    (block) => (!block.eventStatus || block.eventStatus === 'scheduled') &&
             (!block.blockType || block.blockType === 'break')
  );

  // Get all unique provider IDs
  const providerIds = getUniqueProviderIds(confirmedShifts, activeVisits, activeBreaks);

  // Build capacity data for each provider for each day
  providerIds.forEach((providerId) => {
    const provider = providers.get(providerId);

    // Skip if provider metadata not found
    if (!provider) {
      console.warn(`Provider metadata not found for ID: ${providerId}`);
      return;
    }

    dateRange.forEach((date) => {
      const dateKey = formatDateKey(date);

      // Aggregate minutes for this provider on this day
      const shiftMinutes = aggregateShiftMinutes(confirmedShifts, providerId, dateKey);
      const visitMinutes = aggregateVisitMinutes(activeVisits, providerId, dateKey);
      const breakMinutes = aggregateBreakMinutes(activeBreaks, providerId, dateKey);

      // Skip days with no shifts (provider not working)
      if (shiftMinutes === 0) {
        return;
      }

      // Calculate metrics
      const utilization = calculateUtilization(shiftMinutes, visitMinutes, breakMinutes);
      const remainingMinutes = calculateRemainingMinutes(shiftMinutes, visitMinutes, breakMinutes);
      const overCapacity = isOverCapacity(visitMinutes, shiftMinutes, breakMinutes);
      const riskLevel = assessRiskLevel(utilization, remainingMinutes);

      snapshot.push({
        providerId,
        providerName: `${provider.name}`,
        role: provider.role,
        date: dateKey,
        shiftMinutes,
        visitMinutes,
        breakMinutes,
        remainingMinutes,
        utilization,
        isOverCapacity: overCapacity,
        riskLevel,
      });
    });
  });

  return snapshot;
}

/**
 * Filter snapshot to specific date range
 */
export function filterSnapshotByDateRange(
  snapshot: ProviderCapacityDay[],
  startDate: string,
  endDate: string
): ProviderCapacityDay[] {
  return snapshot.filter((day) => day.date >= startDate && day.date <= endDate);
}

/**
 * Filter snapshot to specific role(s)
 */
export function filterSnapshotByRole(
  snapshot: ProviderCapacityDay[],
  roles: string[]
): ProviderCapacityDay[] {
  return snapshot.filter((day) => roles.includes(day.role));
}

/**
 * Sort snapshot by utilization (ascending or descending)
 */
export function sortSnapshotByUtilization(
  snapshot: ProviderCapacityDay[],
  ascending = true
): ProviderCapacityDay[] {
  return [...snapshot].sort((a, b) => {
    return ascending ? a.utilization - b.utilization : b.utilization - a.utilization;
  });
}
